import { useState, useEffect } from 'react';
import { useToast } from '../../components/shared/ToastNotification';
import api from '../../services/api';

interface Route {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

export default function DeliveryRoutesIndex() {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', active: true });
  const [editingRoute, setEditingRoute] = useState<Route | null>(null);
  const { success, error } = useToast();

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      const res = await api.get('/delivery_routes');
      if (res.success) {
        setRoutes(res.data.delivery_routes || []);
      } else {
        error(res.message || 'Failed to load routes');
      }
    } catch (err) {
      error('Failed to load routes');
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isEdit = !!editingRoute;
    const endpoint = isEdit ? `/delivery_routes/${editingRoute.id}` : '/delivery_routes';
    
    try {
      const res = isEdit 
        ? await api.patch(endpoint, { delivery_route: formData })
        : await api.post(endpoint, { delivery_route: formData });

      if (res.success) {
        success(`Route ${isEdit ? 'updated' : 'created'} successfully`);
        setShowForm(false);
        setEditingRoute(null);
        setFormData({ name: '', description: '', active: true });
        fetchRoutes();
      } else {
        error(res.message || (res.errors?.join(', ')) || `Failed to ${isEdit ? 'update' : 'create'} route`);
      }
    } catch (err) {
      error('An error occurred');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this route? This action cannot be undone.')) return;
    try {
      const res = await api.delete(`/delivery_routes/${id}`);
      if (res.success) {
        success('Route deleted successfully');
        fetchRoutes();
      } else {
        error(res.message || (res.errors?.join(', ')) || 'Failed to delete route');
      }
    } catch (err) {
      error('An error occurred');
    }
  };

  const startEdit = (route: Route) => {
    setEditingRoute(route);
    setFormData({ name: route.name, description: route.description, active: route.active });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingRoute(null);
    setFormData({ name: '', description: '', active: true });
  };

  return (
    <div className="pt-6 space-y-8 animate-in fade-in duration-700">
      {/* Premium Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pb-2 border-b border-slate-100">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Delivery <span className="text-indigo-600">Routes</span></h1>
          <p className="text-slate-500 font-medium text-sm flex items-center gap-2">
             <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
             {routes.length} geographic clusters active for global logistics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => showForm ? cancelForm() : setShowForm(true)}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-xs transition-all ${
              showForm 
                ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' 
                : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-xl shadow-indigo-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
            {showForm ? 'Cancel Operation' : 'Initialize New Route'}
          </button>
        </div>
      </div>

      {/* Form Section */}
      {showForm && (
        <div className="bg-white p-8 rounded-[2rem] border-2 border-slate-100 shadow-xl shadow-slate-200/50 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="flex items-center gap-3 mb-8">
             <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
             </div>
             <div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight">{editingRoute ? 'Edit Global Route' : 'Establish New Route'}</h2>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Define geographic bounds and operational status</p>
             </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Route Identifier</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Sector-11 Logistics Hub"
                  className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-bold text-slate-900"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Operational Description</label>
                <input 
                  type="text" 
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Street density mapping or cluster data"
                  className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-bold text-slate-900"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-6 border-t border-slate-100">
               <div className="flex items-center gap-4">
                  {editingRoute && (
                    <label className="relative inline-flex items-center cursor-pointer group">
                      <input 
                        type="checkbox" 
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      <span className="ml-3 text-sm font-black text-slate-700 uppercase tracking-tight">Status: {formData.active ? 'Operational' : 'Deactivated'}</span>
                    </label>
                  )}
               </div>
               <div className="flex gap-3">
                  <button 
                    type="button" 
                    onClick={cancelForm}
                    className="px-8 py-3.5 text-slate-500 font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-all"
                  >
                    Abort
                  </button>
                  <button type="submit" className="px-10 py-3.5 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-slate-200 hover:bg-black transition-all active:scale-95">
                    {editingRoute ? 'Commit Changes' : 'Publish Route'}
                  </button>
               </div>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {loading ? (
          <div className="col-span-full py-24 flex flex-col items-center justify-center space-y-4">
            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Discovering Routes...</p>
          </div>
        ) : routes.length === 0 ? (
          <div className="col-span-full py-20 bg-white rounded-2xl border border-dashed border-slate-200 flex flex-col items-center justify-center space-y-4 shadow-sm">
            <div className="p-4 bg-slate-50 rounded-2xl text-slate-300">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 20l-5.447-2.724A2 2 0 013 15.382V6.418a2 2 0 011.106-1.789L9 2l5 2.5L20 2v8.944a2 2 0 01-1.106 1.789L13 15.5l-4 4.5z" /></svg>
            </div>
            <p className="text-slate-500 font-semibold">No delivery routes mapped yet.</p>
            <button onClick={() => setShowForm(true)} className="text-indigo-600 font-bold text-sm hover:underline">Create First Route</button>
          </div>
        ) : (
          routes.map((route) => (
            <div key={route.id} className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-200 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden flex flex-col">
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300 shadow-sm">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A2 2 0 013 15.382V6.418a2 2 0 011.106-1.789L9 2l5 2.5L20 2v8.944a2 2 0 01-1.106 1.789L13 15.5l-4 4.5z" />
                    </svg>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border transition-colors ${
                    route.active 
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                      : 'bg-slate-50 text-slate-400 border-slate-100'
                  }`}>
                    {route.active ? 'Operational' : 'Paused'}
                  </span>
                </div>

                <div className="flex-1">
                  <h3 className="text-base font-bold text-slate-800 leading-tight group-hover:text-indigo-700 transition-colors mb-2">{route.name}</h3>
                  <p className="text-xs text-slate-500 font-medium line-clamp-2 leading-relaxed h-[36px]">
                    {route.description || 'Optimal delivery cluster for primary customer density.'}
                  </p>
                </div>
              </div>
              
              <div className="px-4 pb-4 flex items-center gap-2 border-t border-slate-50 pt-3 mt-auto bg-slate-50/30">
                <button 
                  onClick={() => startEdit(route)}
                  className="flex-1 py-2 text-xs font-semibold text-slate-600 hover:bg-white hover:shadow-sm rounded-lg transition-all border border-transparent hover:border-slate-100"
                >
                  Configure
                </button>
                <button 
                  onClick={() => handleDelete(route.id)}
                  className="px-3 py-2 bg-rose-50 text-rose-500 rounded-lg hover:bg-rose-500 hover:text-white transition-all border border-rose-100"
                  title="Delete Route"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
